import { useCallback, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

interface StartValidationParams {
  sourceFile: string;
}

interface ValidationResult {
  ingestionId: string;
  validationJobId: string;
}

/**
 * Hook to start file validation and profiling jobs.
 *
 * This hook handles starting validation jobs (header detection, column profiling, etc.)
 *
 * This job will produce:
 * - A clean data file ([file]._VALID_ROWS.csv)
 * - An invalid rows file ([file]._INVALID_ROWS.csv)
 * - A data profile report ([file]._ROW_DETAILS.json)
 *
 * Which can then be used by IngestForm for the actual Snowflake ingestion.
 *
 * NOTE: This does NOT ingest data to Snowflake. It only validates and profiles the file.
 */
export const useStartValidation = () => {
  const { datasetId } = useParams();
  const [searchParams] = useSearchParams();
  const ingestionId = searchParams.get("ingestion");
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startValidation = useCallback(
    async ({ sourceFile }: StartValidationParams): Promise<ValidationResult | null> => {
      if (!datasetId) {
        const errorMsg = "Dataset ID is required";
        setError(errorMsg);
        return null;
      }

      if (!ingestionId) {
        const errorMsg = "Ingestion ID is required";
        setError(errorMsg);
        return null;
      }

      if (!sourceFile) {
        const errorMsg = "Platform storage key is required";
        setError(errorMsg);
        return null;
      }

      setIsStarting(true);
      setError(null);

      try {
        // Start validation job
        // This job will:
        // - Detect headers
        // - Validate data format
        // - Validate rows
        const validationJobResp = await fetch(
          `${VITE_CATALOG_API}/datasets/${datasetId}/ingestions/${ingestionId}/jobs`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              job_type: "validation",
              output_path: `ingestions/${datasetId}/${ingestionId}`,
              arguments: {
                file_uris: [sourceFile],
              },
            }),
          }
        );

        if (!validationJobResp.ok) {
          const errorText = await validationJobResp.text();
          throw new Error(`Failed to start validation job: ${errorText}`);
        }

        // Backend returns plain text with quotes, need to strip them
        let validationJobId = await validationJobResp.text();
        validationJobId = validationJobId.replace(/^"|"$/g, ""); // Remove leading/trailing quotes

        trackEvent("file_validation_started", {
          dataset_id: datasetId,
          ingestion_id: ingestionId,
          validation_job_id: validationJobId,
          source_file: sourceFile,
        });

        return {
          ingestionId,
          validationJobId,
        };
      } catch (err: any) {
        const errorMsg = err.message || "Failed to start validation. Please try again.";
        console.error("Failed to start validation:", err);
        rumClient?.recordError(err);
        setError(errorMsg);
        return null;
      } finally {
        setIsStarting(false);
      }
    },
    [datasetId, ingestionId]
  );

  const clearError = () => setError(null);

  return {
    startValidation,
    isStarting,
    error,
    clearError,
  };
};
